import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { History, PlayCircle, Swords } from 'lucide-react';
import ResultsHome from './ResultsHome';
import BenchmarkRunner from './BenchmarkRunner';

function ComparePage({ user, onOpenLogin, onCreditsUpdate, onBenchmarkComplete }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialTab = searchParams.get('tab') || 'results';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [resultsTotal, setResultsTotal] = useState(0);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'results') {
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };

  const handleBenchmarkCompleteInternal = () => {
    if (onBenchmarkComplete) onBenchmarkComplete();
  };

  const handleBackToResults = () => {
    handleTabChange('results');
  };

  return (
    <div className="min-h-screen">
      {/* Floating Header with Tabs */}
      <div className="px-4 sm:px-0 pb-4">
        <div className="max-w-6xl mx-auto px-0 sm:px-6">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-gray-200/80 shadow-sm sm:shadow-md overflow-hidden">
            <div className="px-4 sm:px-6 py-4">
              {/* Page Title */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                  <Swords className="text-white" size={20} />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">AI Compare</h1>
                  <p className="text-sm text-gray-500">Compare AI models head-to-head</p>
                </div>
              </div>

              {/* Tabs — pastel violet */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleTabChange('results')}
                  className={`flex items-center gap-2 px-4 py-2 font-medium text-sm rounded-lg transition-all ${
                    activeTab === 'results'
                      ? 'bg-violet-50 text-violet-700 shadow-sm border border-violet-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <History size={18} />
                  <span>Results</span>
                  {resultsTotal > 0 && (
                    <span className="bg-violet-500 text-white px-2 py-0.5 rounded-full text-xs font-bold min-w-[22px] text-center">
                      {resultsTotal > 99 ? '99+' : resultsTotal}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => handleTabChange('new')}
                  className={`flex items-center gap-2 px-4 py-2 font-medium text-sm rounded-lg transition-all ${
                    activeTab === 'new'
                      ? 'bg-violet-50 text-violet-700 shadow-sm border border-violet-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <PlayCircle size={18} />
                  <span>New Compare</span>
                </button>
              </div>

              {/* Login prompt */}
              {!user && (
                <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                  <span className="text-orange-500 text-sm">⚠️ Please login to run a comparison</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'results' ? (
          <ResultsHome
            user={user}
            onOpenLogin={onOpenLogin}
            embedded={true}
            onTotalChange={setResultsTotal}
          />
        ) : (
          <BenchmarkRunner
            user={user}
            onCreditsUpdate={onCreditsUpdate}
            onBenchmarkComplete={handleBenchmarkCompleteInternal}
            onBackToResults={handleBackToResults}
          />
        )}
      </div>
    </div>
  );
}

export default ComparePage;